import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { camelToDisplay, capitalize, EntityRollingProgramme, isEntityRollingProgrammeConstructorProps, NodeEdgeMapper } from '@iotv/datamodel-core';
import { AppValueTypes, getObjectHash } from '@iotv/iotv-v3-types';
import { deviceEnabledTypes, isDeviceEnabledType } from '@iotv/payload-definition';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Card, CardContent, Tab } from '@mui/material';
import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getViewDefintionFromObject } from '../../../../../../components/factories/AttributeDefinitions';
import { NodeEdgeMapperContext } from '../../../../../../ContextProviders/NodeEdgeMapperComponentContext';
import { extractViewObjectFromNodeEdgeMapper } from '../../../../../../data/daoFunctions/nodeEdgeMapperTransform';
import { DeviceControlDAO } from '../../../../../../data/DeviceControlDAO';
import { isAdminRole } from '../../../../../../helpers/access';
import { useTabHandlerStringKeys } from '../../../../../../hooks/useTabHandler';
import { useDeviceControlChannelActions } from '../../data/UseDeviceControlChannelActions';
import { EntityControlTransactionsView } from '../../Entity/Components';
import { batchGetLatestDCTs } from '../../EntityGroup/queries/batchGetLatestDCTs';
import { EntityGroupControlContext } from '../../EntityGroup/Reducers/EntityGroupControlReducer';
import { GroupOperationalStatusInternal } from '../../GroupOperationalStatus/Components/GroupOperationalStatusInternal';
import { EntityRollingProgrammeContext, } from '../Reducers/EntityRollingProgrammeReducer';
import { ScheduleStatus } from './Tabs/ScheduleStatus';
import { WindowAllocation } from './Tabs/WindowAllocation';
const debug = process.env.REACT_APP_DEBUG && false;
export const EntityRollingProgrammeInternal = (props) => {
    const { viewObject, transactionFunctions: TransactionFunctions, userFunctions: UserFunctions, userSelectedGroup, isSmallView } = props;
    const canaryString = 'EntityRollingProgrammeSetInternal';
    const [state, dispatch] = useContext(EntityRollingProgrammeContext);
    const { rollingProgramme } = state;
    const { state: { nemHash, nodeEdgeMapper, apexNode }, dispatch: nodeEdgeMapperDispatch, } = useContext(NodeEdgeMapperContext);
    const { state: controlState, dispatch: controlDispatch } = useContext(EntityGroupControlContext);
    const { matchedPrimary } = viewObject;
    const x = useDeviceControlChannelActions({ canaryString: 'EntityRollingProgrammeInternal', loadDeviceConfigs: true });
    const matchedPrimaryHash = getObjectHash(matchedPrimary);
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        if (rollingProgramme && !rollingProgramme.supportedType) {
            console.log(`WARN: No supportedType on rolling programme, redirecting to Details tab`);
            navigate(`${location.pathname}?tabName=Details`);
        }
    }, [rollingProgramme?.sk]);
    /** On change of rolling programme this needs to clear all context contents before reconstituting everything  */
    useEffect(() => {
        if (matchedPrimary?.type === 'EntityRollingProgramme' && isEntityRollingProgrammeConstructorProps(matchedPrimary)) {
            /* if programme has changed or is an intial programme, SetRollingProgramme, this will clear context and start again */
            if (matchedPrimary.sk !== rollingProgramme?.sk || !rollingProgramme) {
                const action = {
                    type: 'SetRollingProgramme',
                    value: { rollingProgramme: new EntityRollingProgramme(matchedPrimary) },
                };
                dispatch(action);
            }
            else {
                const action = { type: 'MutateRollingProgramme', value: { update: matchedPrimary } };
                dispatch(action);
            }
        }
        else {
            if (matchedPrimary) {
                console.log(`WARN: EntityRollingProgrammeSetInternal not setting RollingProgramme because`, matchedPrimary, isEntityRollingProgrammeConstructorProps(matchedPrimary));
            }
        }
    }, [matchedPrimaryHash]);
    const viewDefinition = matchedPrimary ? getViewDefintionFromObject(matchedPrimary) : {};
    viewDefinition.supportedType = {
        key: 'supportedType',
        editable: true,
        label: 'Supported Type',
        defaultValue: matchedPrimary?.supportedType,
        type: AppValueTypes.ENUM,
        enumKV: Object.fromEntries(Object.keys(deviceEnabledTypes).map((k) => [k, k])),
        required: true,
    };
    const Entities = nodeEdgeMapper.getContainedItems().filter(isDeviceEnabledType);
    const entitiesHash = getObjectHash(Object.keys(Entities ?? {}));
    /* on change of entities, get clear entity control context */
    useEffect(() => {
        if (Entities && Object.keys(Entities ?? {}).length > 0) {
            const EntityGroupContext = new NodeEdgeMapper();
            EntityGroupContext.loadItems(Entities);
            const loadAction = { type: 'LoadContext', value: { EntityGroup: null, EntityGroupContext } };
            controlDispatch(loadAction);
            debug && console.log(`EntityRollingProgrammeInternal batchGetLatestDCTs`, apexNode);
            batchGetLatestDCTs({
                Entities,
                addDCTs: (entity, dcts) => {
                    const action = { type: 'AddTransactions', value: { entity, dcts } };
                    controlDispatch(action);
                },
                broadcastScheduleSk: apexNode?.sk,
            });
        }
    }, [entitiesHash]);
    const entityTransactions = Object.values(controlState.EntityStates ?? {});
    const tabDefinitions = {
        WindowAllocation: () => _jsx(WindowAllocation, {}),
        ScheduleStatus: () => _jsx(ScheduleStatus, {}),
        Transactions: () => _jsx(EntityControlTransactionsView, { entityTransactions }),
        'Operational Status': () => rollingProgramme && _jsx(GroupOperationalStatusInternal, { viewObject: extractViewObjectFromNodeEdgeMapper(nodeEdgeMapper, rollingProgramme) }),
        // CRA 2025 05 — disabled as it provides the user with options that they probably shouldn't be fiddling with,
        // as the DetailCard doesn't know the right APIs to use with a RollingProgramme
        // Details: () =>
        //   matchedPrimary ? (
        //     <ObjectDetailCard
        //       {...{
        //         matchedPrimary,
        //         viewDefinition,
        //         mostProximateCustomer: undefined,
        //         transactionFunctions: TransactionFunctions,
        //         userFunctions: UserFunctions,
        //         initialEditMode: ComponentMode.EDIT,
        //       }}
        //     />
        //   ) : null,
    };
    const tabsToDisplay = tabDefinitions;
    const { state: [[tabValue]], handlers: { handleTabChange }, } = useTabHandlerStringKeys(tabsToDisplay);
    /** on change of rolling programme  */
    useEffect(() => {
        if (rollingProgramme) {
            const apexNodeAction = { type: 'setApexNode', value: { apexNode: rollingProgramme } };
            nodeEdgeMapperDispatch(apexNodeAction);
            DeviceControlDAO.device.control.programme.rolling.getContext({ rollingProgramme }).then(({ err, data }) => {
                if (data) {
                    const { nodeEdgeMapper, apexNode } = data;
                    const action = { type: 'loadNEMContents', value: { canaryString, nodeEdgeMapper } };
                    nodeEdgeMapperDispatch(action);
                }
            });
        }
    }, [rollingProgramme?.sk /*nemHash*/]);
    const viewSizeWrapper = (content) => {
        // unsetting overflow (on the container) is required to make `position: sticky` work
        return !isSmallView ? (_jsx(Card, { elevation: 3, sx: { overflow: 'unset' }, children: _jsx(CardContent, { sx: { padding: 0, ['&:last-child']: { padding: 2 } }, children: content }) })) : (content);
    };
    return isAdminRole(userSelectedGroup) ? (viewSizeWrapper(_jsxs(TabContext, { value: tabValue, children: [_jsx(TabList, { onChange: handleTabChange, children: Object.entries(tabsToDisplay)
                    .filter(([_, RFC], i) => RFC !== null)
                    .map(([tabName, _RFC], i) => (_jsx(Tab, { value: tabName, label: capitalize(camelToDisplay(tabName)), disabled: false }, `tabHeader_${i}`))) }), Object.entries(tabsToDisplay)
                .filter(([_k, c]) => !!c)
                .map(([k, component], index) => (_jsx(TabPanel, { value: k, sx: { padding: 1 }, children: component() }, `tabBase_${k}`)))] }))) : (_jsx(WindowAllocation, {}));
};
